import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  Shield, X, LayoutDashboard, ScrollText, Settings,
  Headset, Mail, Users, FileText, Coins, Gift, BarChart3, ArrowRightLeft, Home, MessageSquare, Send
} from "lucide-react";
import { useAuth } from "../App";
import { useGuardianStatus } from "../hooks/useGuardianStatus";

const SHIELD_TONE = {
  GREEN: "text-white/50 hover:text-white",
  AMBER: "text-amber-300 hover:text-amber-200",
  RED: "text-red-400 hover:text-red-300",
  HIGH_ALERT: "text-red-300 hover:text-red-200 admin-shield-flash",
};

const menuSections = [
  {
    label: "",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: Home },
    ]
  },
  {
    label: "Admin",
    items: [
      { to: "/admin", label: "Admin Dash", icon: LayoutDashboard },
      { to: "/admin?tab=prompts", label: "Prompts", icon: ScrollText },
      { to: "/admin?tab=settings", label: "Settings", icon: Settings },
    ]
  },
  {
    label: "Support",
    items: [
      { to: "/helpdesk", label: "Help Desk", icon: Headset },
      { to: "/forum", label: "Forum", icon: MessageSquare },
    ]
  },
  {
    label: "Subscribers",
    items: [
      { to: "/mailer", label: "eMailing List", icon: Mail },
      { to: "/waitlist", label: "Waitlist", icon: Users },
    ]
  },
  {
    label: "Payouts",
    items: [
      { to: "/admin/payouts", label: "Payout Batches", icon: Send },
      { to: "/plog/commission_ledger_v2", label: "Commission Ledger V2", icon: ArrowRightLeft },
      { to: "/plog/audit", label: "Commission Audit", icon: Shield },
    ]
  },
  {
    label: "Logs",
    items: [
      { to: "/plog", label: "Prompt Logs", icon: FileText },
      { to: "/plog/tokens", label: "Tokens Use", icon: ScrollText },
      { to: "/plog/credits", label: "Coin Ledger", icon: Coins },
      { to: "/plog/bonus-pool", label: "Coin Bonus", icon: Gift },
      { to: "/plog/aff_acc", label: "Affiliate Ledger (legacy)", icon: ArrowRightLeft },
    ]
  },
  {
    label: "Analytics",
    items: [
      { to: "/admin/analytics", label: "Command Center", icon: BarChart3 },
      { to: "/mailer?tab=analytics", label: "eMailer", icon: BarChart3 },
    ]
  },
];

export default function AdminMenu() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  // Only poll for admins — regular users never see the shield.
  const guardian = useGuardianStatus({ enabled: isAdmin });
  const tone = SHIELD_TONE[guardian.code] || SHIELD_TONE.GREEN;
  const flashing = guardian.code === "HIGH_ALERT";

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  // Only render for admin users
  if (!isAdmin) return null;

  const isActive = (to) => {
    const [path, query] = to.split("?");
    if (location.pathname !== path) return false;
    if (!query) return !location.search;
    return location.search === `?${query}`;
  };

  return (
    <div ref={menuRef} className="relative" data-testid="admin-menu">
      {/* Guardian flash keyframe injected inline so we don't touch tailwind.config */}
      {flashing && (
        <style>{`
          @keyframes admin-shield-flash { 0%,100% { filter: drop-shadow(0 0 0 rgba(239,68,68,0)); } 50% { filter: drop-shadow(0 0 6px rgba(239,68,68,0.9)); } }
          .admin-shield-flash { animation: admin-shield-flash 1.1s ease-in-out infinite; }
        `}</style>
      )}
      <button
        onClick={() => setOpen(!open)}
        className={`p-2 rounded-lg hover:bg-white/10 transition-colors ${tone}`}
        data-testid="admin-menu-toggle"
        data-guardian-code={guardian.code}
        title={guardian.headline ? `Guardian: ${guardian.headline}` : "Admin Menu"}
      >
        {open ? <X className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-divine-black-light border border-white/10 rounded-xl shadow-2xl z-[100] overflow-hidden">
          <div className="px-3 py-2 border-b border-white/10">
            <span className="text-xs font-semibold text-white/40 uppercase tracking-wider">Admin Menu</span>
          </div>
          <nav className="py-1 max-h-[70vh] overflow-y-auto">
            {menuSections.map((section, si) => (
              <div key={section.label}>
                {si > 0 && <div className="border-t border-white/5 my-1" />}
                {section.label && (
                  <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-white/30 uppercase tracking-wider">
                    {section.label}
                  </p>
                )}
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      data-testid={`admin-menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                        active
                          ? "text-divine-gold bg-divine-gold/10"
                          : "text-white/60 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>
        </div>
      )}
    </div>
  );
}
